import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {Column} from 'material-table';
import {
  APP,
  DEFAULT_BLACK_COLOR,
  DEFAULT_WHITE_COLOR
} from 'src/style/variable';
import {PAGINATION_SIZE} from 'src/constants/SystemConstants';
import './EligibilityVerificationLogsDialog.scss';
import {
  getInsuranceVerificationLogs,
  getVerificationLogDetails
} from '@shared/services/InsuranceEligibilityServices';
import HelperUtils from '@shared/utils/HelperUtils';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {INSURANCE_SOURCE_MAPPER} from '@shared/constants/Mapping';
import AgGridTable from '@components/system/AgGridTable/AgGridTable';

const COMMON_HEADER_STYLE = {
  color: DEFAULT_BLACK_COLOR,
  backgroundColor: DEFAULT_WHITE_COLOR,
  fontFamily: APP.FONT_FAMILY.MEDIUM,
  width: '15%',
  fontSize: '1rem'
};

const COMMON_CELL_STYLE = {
  width: '15%',
  minWidth: '10rem',
  fontFamily: APP.FONT_FAMILY.REGULAR,
  fontSize: '0.875rem'
};

interface EligibilityVerificationLogsDialogProps {
  open: boolean;
  onClose: () => void;
  groupId: number;
  appointmentId: number;
  clinicId: number;
}
const EligibilityVerificationLogs = (
  props: EligibilityVerificationLogsDialogProps
): JSX.Element => {
  const {open, onClose, clinicId, groupId, appointmentId} = props;
  const {t} = useTranslation();
  const tableRef = React.useRef();
  const {enqueueSnackbar} = useSnackbar();
  const [logsState, setLogsState] = React.useState({
    isLoadingLogs: false,
    logs: []
  });
  const [logDetails, setLogDetails] = React.useState<{
    input_data: Array<{label: string; value: string | number}>;
    output_data: Array<{label: string; value: string | number}>;
    request_d0_string: string | null;
    response_d0_string: string | null;
    created_at: string;
  } | null>(null);
  const [logDetailsViewMode, setLogDetailsViewMode] = React.useState<
    'request' | 'response'
  >('response');
  const [isLoadingLogDetails, setIsLoadingLogDetails] = React.useState(false);

  const fetchInsuranceVerificationLogs = async () => {
    setLogsState(p => ({...p, isLoadingLogs: true}));
    try {
      const res = await getInsuranceVerificationLogs({
        params: {
          clinicGroupId: groupId,
          appointmentId,
          clinicId
        }
      });
      if (Array.isArray(res?.data)) {
        setLogsState(p => ({...p, logs: res.data}));
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
    } finally {
      setLogsState(p => ({...p, isLoadingLogs: false}));
    }
  };

  React.useEffect(() => {
    fetchInsuranceVerificationLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInsuranceSourceLabel = (source: string) => {
    if (source) {
      if (source in INSURANCE_SOURCE_MAPPER)
        return t(
          INSURANCE_SOURCE_MAPPER[
            source as keyof typeof INSURANCE_SOURCE_MAPPER
          ].label
        );
      else return source;
    }
    return '-';
  };

  const handleViewRequest = async (row: any) => {
    if (!row?.id) return;
    setIsLoadingLogDetails(true);
    setLogDetails(null);
    try {
      const res = await getVerificationLogDetails({
        clinicGroupId: groupId,
        clinicId,
        appointmentId,
        logId: row.id
      });
      if (res?.data) {
        setLogDetails(res.data);
        setLogDetailsViewMode('request');
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
    } finally {
      setIsLoadingLogDetails(false);
    }
  };

  const handleViewResponse = async (row: any) => {
    if (!row?.id) return;
    setIsLoadingLogDetails(true);
    setLogDetails(null);
    try {
      const res = await getVerificationLogDetails({
        clinicGroupId: groupId,
        clinicId,
        appointmentId,
        logId: row.id
      });
      if (res?.data) {
        setLogDetails(res.data);
        setLogDetailsViewMode('response');
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
    } finally {
      setIsLoadingLogDetails(false);
    }
  };

  const columnList: Array<Column<any>> = [
    {
      title: t('ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.PAYER_NAME'),
      sorting: false,
      field: 'payer_name',
      headerStyle: COMMON_HEADER_STYLE,
      cellStyle: COMMON_CELL_STYLE,
      render: (row: any) => {
        return <div>{row?.payer_name || '-'}</div>;
      }
    },
    {
      title: t(
        'ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.SUBSCRIBER_GROUP_NUMBER'
      ),
      sorting: false,
      field: 'subscriber_group_no',
      headerStyle: COMMON_HEADER_STYLE,
      cellStyle: COMMON_CELL_STYLE,
      render: (row: any) => {
        return <div>{row?.subscriber_group_no || '-'}</div>;
      }
    },
    {
      title: t(
        'ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.SUBSCRIBER_MEMBER_NUMBER'
      ),
      sorting: false,
      field: 'subscriber_member_no',
      headerStyle: {
        ...COMMON_HEADER_STYLE,
        width: '20%'
      },
      cellStyle: {
        ...COMMON_CELL_STYLE,
        width: '20%',
        minWidth: '15rem'
      },
      render: (row: any) => {
        return <div>{row?.subscriber_member_no || '-'}</div>;
      }
    },
    {
      title: t('ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.SOURCE'),
      sorting: false,
      field: 'source',
      headerStyle: {
        ...COMMON_HEADER_STYLE,
        width: '10%'
      },
      cellStyle: {
        ...COMMON_CELL_STYLE,
        width: '10%',
        minWidth: '10rem'
      },
      render: (row: any) => {
        return (
          <div>{row?.source ? getInsuranceSourceLabel(row.source) : '-'}</div>
        );
      }
    },
    {
      title: t('ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.MESSAGE'),
      sorting: false,
      field: 'message',
      cellStyle: {
        minWidth: '20rem'
      },
      render: (row: any) => {
        return <div>{row?.message || '-'}</div>;
      }
    },
    {
      title: t('ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.VERIFIED_AT'),
      sorting: false,
      field: 'created_at',
      headerStyle: COMMON_HEADER_STYLE,
      cellStyle: COMMON_CELL_STYLE,
      render: (row: any) => {
        return (
          <div>
            {row?.created_at
              ? HelperUtils.formatDisplayDateTime(row?.created_at)
              : '-'}
          </div>
        );
      }
    },
    {
      title: t('COMMON.ACTIONS'),
      sorting: false,
      field: 'request_id',
      headerStyle: {...COMMON_HEADER_STYLE, width: '12%'},
      cellStyle: COMMON_CELL_STYLE,
      render: (row: any) => {
        if (!row?.request_id) {
          return <div>-</div>;
        }
        return (
          <div style={{lineHeight: 2.8}}>
            <span
              className="link-text-style"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                handleViewRequest(row);
              }}>
              {t('View Request')}
            </span>
            <span style={{marginLeft: '0.5rem'}}>|</span>
            <span
              className="link-text-style"
              style={{marginLeft: '0.5rem'}}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                handleViewResponse(row);
              }}>
              {t('View Response')}
            </span>
          </div>
        );
      }
    }
  ];

  const detailsLineData =
    logDetailsViewMode === 'request'
      ? logDetails?.input_data ?? []
      : logDetails?.output_data ?? [];
  const detailsD0String =
    logDetailsViewMode === 'request'
      ? logDetails?.request_d0_string ?? ''
      : logDetails?.response_d0_string ?? '';

  return (
    <Dialog
      maxWidth={'lg'}
      fullWidth={true}
      open={open}
      onClose={onClose}
      className="eligibility-verification-logs-dialog">
      <DialogTitle disableTypography>
        <p>{t('ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.TITLE')}</p>
      </DialogTitle>
      <DialogContent dividers className="content-wrapper">
        <GridTable
          isLoading={logsState.isLoadingLogs}
          tableRef={tableRef}
          columns={columnList}
          localization={{
            body: {
              emptyDataSourceMessage: t(
                'ELIGIBILITY_VERIFICATION.VERIFICATION_LOGS_DIALOG.NO_LOGS'
              )
            }
          }}
          data={logsState.logs}
          options={{
            ...DEFAULT_GRID_TABLE__OPTIONS,
            search: false,
            showTitle: false,
            toolbar: false,
            sorting: true,
            paging: true,
            emptyRowsWhenPaging: false,
            pageSize: PAGINATION_SIZE.ELIGIBILITY_VERIFICATION_LOGS,
            showEmptyDataSourceMessage: true,
            maxBodyHeight: '100%',
            tableLayout: 'auto',
            selection: false,
            draggable: false,
            headerStyle: {
              color: DEFAULT_BLACK_COLOR,
              fontFamily: APP.FONT_FAMILY.MEDIUM,
              flexDirection: 'row'
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow:
              '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
            marginTop: '0.5rem',
            flex: 1
          }}
        />
        {isLoadingLogDetails && (
          <div className="eligibility-log-details-loading">
            {t('COMMON.LOADING')}...
          </div>
        )}
        {logDetails && !isLoadingLogDetails && (
          <div className="eligibility-log-details-panel">
            <div className="eligibility-log-details-header">
              {logDetailsViewMode === 'request'
                ? `${t('View Request')} (${logDetails.created_at ? HelperUtils.formatDisplayDateTime(logDetails.created_at) : ''})`
                : `${t('View Response')} (${logDetails.created_at ? HelperUtils.formatDisplayDateTime(logDetails.created_at) : ''})`}
            </div>
            <div className="eligibility-log-details-content">
              <div className="eligibility-log-details-grid">
                <AgGridTable
                  columns={[
                    {
                      headerName: 'Title',
                      field: 'label',
                      flex: 0.5,
                      cellRenderer: (rowData: any) => (
                        <div>{rowData?.data?.label ?? '-'}</div>
                      )
                    },
                    {
                      headerName:
                        logDetailsViewMode === 'request' ? 'Request' : 'Response',
                      field: 'value',
                      flex: 0.5,
                      cellRenderer: (rowData: any) => (
                        <div>{rowData?.data?.value ?? '-'}</div>
                      )
                    }
                  ]}
                  data={(detailsLineData || []).map((item: any) => ({
                    label: item?.label ?? '-',
                    value: item?.value ?? '-'
                  }))}
                />
              </div>
              <div className="eligibility-log-details-d0-string">
                <div className="eligibility-log-details-d0-string-header">
                  {logDetailsViewMode === 'request'
                    ? t('Request D0 String')
                    : t('Response D0 String')}
                </div>
                <pre className="eligibility-log-details-d0-string-value">
                  {detailsD0String || '-'}
                </pre>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant={'contained'} onClick={onClose} color="primary">
          {t('COMMON.CLOSE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EligibilityVerificationLogs;
